import React, { useCallback, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Clickable, PRESS_PRESETS } from './Clickable';
import { SvgIcon } from './SvgIcon';
import { useTranslation } from '../../shared/i18n';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { centeredLabelText } from '../../shared/theme/sharedStyles';
import {
  buildSupportWhatsappMessage,
  issueContextFromApiError,
  isIssueReportWhatsappEnabled,
  openWhatsappSupport,
  reportIssue,
  type IssueReportContext,
} from '../../platform/support/issueReport';
import { useAuthStore } from '../../shared/auth/store';

const WHATSAPP_BTN_SIZE = 54;
const WHATSAPP_ICON_SIZE = 26;

export type IssueReportStateProps = {
  issue_context: IssueReportContext;
  title?: string;
  message?: string;
  onRetry?: () => void;
  customer_id?: number;
  error?: unknown;
  /** When false, skips auto local log (e.g. already logged by API interceptor). Default true. */
  auto_log?: boolean;
  compact?: boolean;
};

function createStyles(c: ThemeColors, compact: boolean) {
  return StyleSheet.create({
    container: {
      flex: compact ? undefined : 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: compact ? appTheme.spacing.xxl : appTheme.spacing.xl,
      paddingHorizontal: appTheme.spacing.lg,
    },
    iconCircle: {
      width: compact ? 56 : 72,
      height: compact ? 56 : 72,
      borderRadius: compact ? 28 : 36,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.surfaceLight,
      marginBottom: appTheme.spacing.md,
    },
    title: {
      ...centeredLabelText,
      ...(compact ? appTheme.typography.h3 : appTheme.typography.h3),
      color: c.text,
      marginTop: compact ? 0 : appTheme.spacing.sm,
      marginBottom: appTheme.spacing.sm,
    },
    message: {
      ...centeredLabelText,
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      lineHeight: 22,
      maxWidth: 320,
      marginBottom: appTheme.spacing.lg,
    },
    retryButton: {
      backgroundColor: c.primary,
      paddingHorizontal: appTheme.spacing.xl,
      paddingVertical: appTheme.spacing.md,
      borderRadius: appTheme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: appTheme.spacing.lg,
    },
    retryButtonText: {
      ...centeredLabelText,
      ...appTheme.typography.body,
      color: c.onPrimary,
      fontWeight: '600',
    },
    whatsappLabel: {
      ...appTheme.typography.captionSmall,
      fontSize: 12,
      fontWeight: '600',
      color: c.textSecondary,
      textAlign: 'center',
      marginBottom: appTheme.spacing.md,
    },
    whatsappBtn: {
      width: WHATSAPP_BTN_SIZE,
      height: WHATSAPP_BTN_SIZE,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: c.surface,
      ...appTheme.shadows.xs,
    },
  });
}

export const IssueReportState: React.FC<IssueReportStateProps> = ({
  issue_context,
  title,
  message,
  onRetry,
  customer_id,
  error,
  auto_log = true,
  compact = false,
}) => {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createStyles(colors, compact), [colors, compact]);
  const auth_customer_id = useAuthStore(s => s.user?.id);
  const resolved_customer_id = customer_id ?? auth_customer_id;

  const merged_context = useMemo(() => {
    if (!error) {
      return issue_context;
    }
    return issueContextFromApiError(issue_context.page, issue_context.issue, error, {
      api_endpoint: issue_context.api_endpoint,
      http_method: issue_context.http_method,
      kind: issue_context.kind,
    });
  }, [error, issue_context]);

  useEffect(() => {
    if (auto_log) {
      reportIssue(merged_context, resolved_customer_id);
    }
  }, [auto_log, merged_context, resolved_customer_id]);

  const display_title = title ?? t('account.preferencesUnavailableTitle');
  const display_message = message ?? t('account.preferencesUnavailableMessage');
  const show_whatsapp_support = isIssueReportWhatsappEnabled();

  const open_whatsapp = useCallback(() => {
    if (!show_whatsapp_support) {
      return;
    }
    const whatsapp_message = buildSupportWhatsappMessage(merged_context, resolved_customer_id);
    void openWhatsappSupport(whatsapp_message);
  }, [merged_context, resolved_customer_id, show_whatsapp_support]);

  return (
    <View style={styles.container}>
      <View style={styles.iconCircle}>
        <SvgIcon name="alert" size={compact ? 28 : 40} color={colors.warning} />
      </View>
      <Text style={styles.title}>{display_title}</Text>
      <Text style={styles.message}>{display_message}</Text>
      {onRetry ? (
        <Clickable style={styles.retryButton} onPress={onRetry} accessibilityLabel={t('common.retry')} accessibilityRole="button" haptics>
          <Text style={styles.retryButtonText}>{t('common.retry')}</Text>
        </Clickable>
      ) : null}
      {show_whatsapp_support ? (
        <>
          <Text style={styles.whatsappLabel}>{t('account.contactSupportWhatsApp')}</Text>
          <Clickable
            onPress={open_whatsapp}
            accessibilityLabel={t('account.contactSupportWhatsApp')}
            accessibilityRole="link"
            pressScale={PRESS_PRESETS.listRow.scale}
            haptics>
            <View style={styles.whatsappBtn}>
              <SvgIcon name="whatsapp" size={WHATSAPP_ICON_SIZE} color="#25D366" />
            </View>
          </Clickable>
        </>
      ) : null}
    </View>
  );
};

export default IssueReportState;
